import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { isWakeUnit, wakeLabel, secondsFromSave, rowNotes } from '../../osaWakeSource';

// Re-source a failed multishot shot from a neighbouring gyro record.
//
// The tool logs a gyro record every few seconds, but a shot is only saved
// against the first record at or after the operator's depth mark. If that mark
// lands while the tool is still settling the shot fails QA even though the
// records either side are clean. This modal lists the neighbours with a full
// orientation solution recomputed server-side from the survey's calibration, so
// the choice is made on the numbers rather than by stepping blindly.
//
// QA alone is not a sufficient guide — a neighbour can pass GTotal/WTotal while
// sitting well off the azimuth trend of the surrounding shots — so inclination
// and azimuth are shown alongside their delta from the current record.
//
// An OSA Gyro shot is solved from a whole wake, not a record, and the server
// answers in wakes (`Unit: 'wake'`). The customer is told none of that (Nick,
// 2026-10-12): five rows labelled by their offset from the save, and one
// instruction - pick the shot whose WTotal is closest to expected. Wakes, bursts,
// motor positions, pairing and the look-ahead's reasoning all stay in the
// response and out of the dialog. See osaWakeSource.js.
export const ShotSourceModal = ({ isOpen, surveyKey, shotId, WTolerance, onClose, onSurveyUpdated }) => {
    const [data, setData] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [window, setWindow] = useState(5);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const loadCandidates = useCallback(async () => {
        if (!isOpen || !surveyKey || !shotId) return;
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`api/surveys/${surveyKey}/shot/${encodeURIComponent(shotId)}/candidates?window=${window}`);
            if (!response.ok) {
                setError(await response.text() || 'Could not load nearby gyro records.');
                setData(null);
                return;
            }
            const result = await response.json();
            setData(result);
            setSelectedIndex(result.CurrentIndex);
        } catch (e) {
            setError('Could not load nearby gyro records.');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [isOpen, surveyKey, shotId, window]);

    useEffect(() => { loadCandidates(); }, [loadCandidates]);

    // Reset the widened view each time the modal is opened for a new shot.
    useEffect(() => { if (isOpen) setWindow(5); }, [isOpen, shotId]);

    const apply = async (body) => {
        setSaving(true);
        setError(null);
        try {
            const response = await fetch(`api/surveys/${surveyKey}/shot/${encodeURIComponent(shotId)}/source`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            if (!response.ok) {
                setError(await response.text() || 'Could not update the shot.');
                return;
            }
            const survey = await response.json();
            if (onSurveyUpdated) onSurveyUpdated(survey);
            onClose();
        } catch (e) {
            setError('Could not update the shot.');
        } finally {
            setSaving(false);
        }
    };

    // The server's verdict, not a recomputed one. It has just solved these
    // records and is the only side that knows whether the tool has a bore-axis
    // gyro and whether this survey's latitude is usable — both of which decide
    // QA on a two-axis tool and neither of which is visible from a candidate row.
    const qaPass = (candidate) => !!candidate.QaPass;

    const wakes = isWakeUnit(data);
    const current = data && data.Candidates.find(c => c.IsCurrent);
    const byIndex = (index) => data && data.Candidates.find(c => c.Index === index);

    // Sitting off the original record no longer implies a person moved it: from
    // app v2.4 the tool searches each shot's stationary period and picks for
    // itself, so most shots are off it. EditedBy is what separates the two.
    const userEdited = !!data && data.EditedBy !== undefined && data.EditedBy !== null;
    const by = userEdited && data.EditedBy ? <> by <strong>{data.EditedBy}</strong></> : null;
    const provenance = !data ? null
        : wakes
            ? userEdited
                ? <>Re-sourced{by}.</>
                : null
            : userEdited && data.IsEdited
                ? <>Re-sourced from <strong>#{data.OriginalIndex}</strong>{by}.</>
                : userEdited
                    // Moved and then put back: current is the original again.
                    ? <>Reset to the first record after the depth mark{by}.</>
                    : data.IsEdited
                        ? <>The tool selected it in preference to <strong>#{data.OriginalIndex}</strong>, the
                            first record after the depth mark.</>
                        : null;
    // Re-saving the record the shot is already on is normally a no-op, and the
    // Apply button stays disabled for it. Not on a MiNS survey merged before
    // Navigator v31: those azimuths were solved from the Earth-rate magnitude
    // rather than the levelled vertical-rate identity, and on the Sierra Minera
    // Caraz runs that is worth up to 77 degrees on a station whose record is
    // perfectly good. The server compares the stored answer against a fresh solve
    // and says so, rather than this side guessing from the tool type.
    const recalculates = !!(data && data.Recalculates);
    const changed = data && (selectedIndex !== data.CurrentIndex || recalculates);
    const atMaxWindow = window >= 25;
    // An empty wake has nothing to solve, so the server refuses it; so does this.
    const selectable = (candidate) => !(wakes && candidate.Empty);
    const selected = data && byIndex(selectedIndex);
    const canApply = changed && (!selected || selectable(selected));

    // What the tool's own selection would pick. For records, MiNS only: an AQ
    // shot was already selected on the handset, so there the stored record is the
    // tool's answer and this would just re-run it. For OSA wakes, every shot: the
    // backlog never had a look-ahead, and on a newer shot it should agree with
    // the tool, which the server checks.
    const recommended = data && data.Recommended !== null && data.Recommended !== undefined
        ? data.Recommended : null;
    const recommendable = recommended !== null && !data.RecommendedUnrepaired;
    const wTolerance = data && data.WTolerance !== undefined && data.WTolerance !== null
        ? data.WTolerance : WTolerance;

    // Signed difference in degrees, wrapped to +/-180 so an azimuth either side
    // of north reads as a small step rather than a ~360 jump.
    const angleDelta = (value, reference) => {
        let delta = value - reference;
        while (delta > 180) delta -= 360;
        while (delta < -180) delta += 360;
        return delta;
    };

    const formatDelta = (delta) => `${delta >= 0 ? '+' : ''}${delta.toFixed(2)}`;

    // A delta from the current row means nothing when the current row is an
    // empty wake: its numbers were solved from a zero-filled placeholder.
    const showDeltas = current && !(wakes && current.Empty);

    const recordBanners = data && !wakes && (
        <>
            {recommendable && (
                <div className="alert alert-secondary" role="alert">
                    The tool would pick record <strong>#{recommended}</strong>
                    {recommended === data.CurrentIndex
                        ? ', which is the one this shot is already on.'
                        : ', judging it against the rest of this shot’s stationary period.'}
                </div>
            )}
            {recommended !== null && data.RecommendedUnrepaired && (
                <div className="alert alert-warning" role="alert">
                    No record in this shot&rsquo;s stationary period reads like a tool that was
                    actually still, so there is nothing here to recommend. The station most likely
                    needs re-shooting rather than re-sourcing.
                </div>
            )}
        </>
    );

    const wakeBanners = data && wakes && data.CurrentEmpty && (
        <div className="alert alert-danger" role="alert">
            This shot was taken while the tool was moving. Choose another below.
        </div>
    );

    const wakeIntro = data && wakes && (
        <p className="text-muted">
            Choose the shot whose WTotal is closest to <strong>{data.ExpectedWTotal.toFixed(2)}</strong>°/hr
            {wTolerance !== undefined && <> &plusmn; {wTolerance.toFixed(2)}</>}.{' '}
            {provenance}
        </p>
    );

    const recordIntro = data && !wakes && (
        <p className="text-muted">
            This shot is calculated from gyro record <strong>#{data.CurrentIndex}</strong>
            {' '}of {data.GyroRecordCount}. {provenance}{' '}
            Expected WTotal for this survey is{' '}
            <strong>{data.ExpectedWTotal.toFixed(2)}</strong>°/hr
            {wTolerance !== undefined && <> &plusmn; {wTolerance.toFixed(2)}</>}.
            Pick the record whose inclination and azimuth sit closest to the trend of the
            surrounding shots — a passing QA mark on its own is not enough.
        </p>
    );

    const resetLabel = !data ? ''
        : wakes
            ? 'Reset to original'
            : userEdited
                ? `Reset to original (#${data.OriginalIndex})`
                : `Use first record after mark (#${data.OriginalIndex})`;

    const applyLabel = !data ? ''
        : saving
            ? 'Saving...'
            : recalculates && selectedIndex === data.CurrentIndex
                ? (wakes ? 'Recalculate' : `Recalculate #${selectedIndex}`)
                : (wakes ? 'Use selected' : `Use #${selectedIndex}`);

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="lg">
            <ModalHeader toggle={onClose}>
                Re-source shot{data ? ` at ${data.Depth.toFixed(2)} m` : ''}
            </ModalHeader>
            <ModalBody>
                {loading && <p>{wakes ? 'Loading...' : 'Loading nearby gyro records...'}</p>}
                {error && <div className="alert alert-danger" role="alert">{error}</div>}
                {data && (
                    <div>
                        {data.Unresolvable && wakes && (
                            <div className="alert alert-warning" role="alert">
                                At <strong>{current ? current.Inclination.toFixed(1) : '?'}&deg;</strong> inclination
                                the azimuth cannot be resolved, so no shot below will pass QA.
                            </div>
                        )}
                        {recalculates && wakes && (
                            <div className="alert alert-info" role="alert">
                                Recalculated, this shot reads{' '}
                                <strong>{current ? current.Azimuth.toFixed(2) : '?'}&deg;</strong> rather than the
                                stored <strong>{data.StoredAzimuth !== null && data.StoredAzimuth !== undefined ? data.StoredAzimuth.toFixed(2) : '?'}&deg;</strong>.
                            </div>
                        )}
                        {data.Unresolvable && !wakes && (
                            <div className="alert alert-warning" role="alert">
                                This tool has no bore gyro. At{' '}
                                <strong>{current ? current.Inclination.toFixed(1) : '?'}&deg;</strong> inclination
                                its azimuth is reconstructed from gravity and cannot be resolved, so every
                                {' record'} below fails QA for the same reason. Re-sourcing will not fix this shot,
                                and the azimuth and WTotal columns below are not measurements of anything &mdash;
                                the missing bore channel is carrying most of the Earth rate at this inclination.
                            </div>
                        )}
                        {recalculates && !wakes && (
                            <div className="alert alert-info" role="alert">
                                Recalculated from the same {'gyro record'}, this shot reads{' '}
                                <strong>{current ? current.Azimuth.toFixed(2) : '?'}&deg;</strong>
                                {data.StoredAzimuth !== null && data.StoredAzimuth !== undefined && <>
                                    {' '}against the <strong>{data.StoredAzimuth.toFixed(2)}&deg;</strong> stored
                                    against it</>} &mdash; this survey was solved by an older version of the tool
                                software. Every row below is a fresh solve; applying one is what writes the
                                corrected value back.
                            </div>
                        )}
                        {recordBanners}
                        {wakeBanners}
                        {recordIntro}
                        {wakeIntro}
                        <table className="table table-sm table-hover">
                            <thead>
                                <tr>
                                    <th style={{ width: '2.5rem' }}></th>
                                    <th>{wakes ? 'Shot' : '#'}</th>
                                    <th>Time</th>
                                    <th className="text-end">Inclination</th>
                                    <th className="text-end">Azimuth</th>
                                    <th className="text-end">GTotal</th>
                                    <th className="text-end">WTotal</th>
                                    <th className="text-center">QA</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.Candidates.map((candidate) => {
                                    const isSelected = candidate.Index === selectedIndex;
                                    const canPick = selectable(candidate);
                                    const pick = () => { if (canPick) setSelectedIndex(candidate.Index); };
                                    const notes = wakes ? rowNotes(candidate, data) : [];
                                    return (
                                        <tr key={candidate.Index}
                                            onClick={pick}
                                            className={isSelected ? 'table-primary' : (canPick ? '' : 'text-muted')}
                                            style={{ cursor: canPick ? 'pointer' : 'not-allowed' }}>
                                            <td>
                                                <input
                                                    type="radio"
                                                    name="gyroRecord"
                                                    checked={isSelected}
                                                    disabled={!canPick}
                                                    onChange={pick}
                                                />
                                            </td>
                                            <td>
                                                {wakes
                                                    ? wakeLabel(candidate.Offset)
                                                    : candidate.Index}
                                                {candidate.IsCurrent && <span className="badge bg-secondary ms-1">current</span>}
                                                {candidate.IsOriginal && !candidate.IsCurrent && <span className="badge bg-light text-dark ms-1">original</span>}
                                                {recommendable && candidate.Index === recommended && <span className="badge bg-success ms-1">recommended</span>}
                                                {notes.map(note => (
                                                    <span key={note} className="badge bg-warning text-dark ms-1">{note}</span>
                                                ))}
                                            </td>
                                            <td>
                                                {wakes
                                                    ? secondsFromSave(candidate.SecondsFromSave)
                                                    : candidate.TimeStamp ? new Date(candidate.TimeStamp).toLocaleTimeString() : ''}
                                            </td>
                                            <td className="text-end">
                                                {candidate.Inclination.toFixed(2)}
                                                {showDeltas && !candidate.IsCurrent && (
                                                    <small className="text-muted ms-1">
                                                        ({formatDelta(angleDelta(candidate.Inclination, current.Inclination))})
                                                    </small>
                                                )}
                                            </td>
                                            <td className="text-end">
                                                {candidate.Azimuth.toFixed(2)}
                                                {showDeltas && !candidate.IsCurrent && (
                                                    <small className="text-muted ms-1">
                                                        ({formatDelta(angleDelta(candidate.Azimuth, current.Azimuth))})
                                                    </small>
                                                )}
                                            </td>
                                            <td className="text-end">{candidate.GTotal.toFixed(4)}</td>
                                            <td className="text-end">{candidate.WTotal.toFixed(2)}</td>
                                            <td className="text-center">
                                                {qaPass(candidate)
                                                    ? <span style={{ color: 'green' }}>✔</span>
                                                    : <span style={{ color: 'red' }}>✘</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {/* The wake window is fixed: it is the look-ahead's whole range
                            plus the two wakes before the save. */}
                        {!wakes && !atMaxWindow && (
                            <Button color="link" size="sm" onClick={() => setWindow(window >= 10 ? 25 : 10)}>
                                Show more records
                            </Button>
                        )}
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                {/* Offered whenever the shot is off the pre-optimiser record, whoever moved
                    it there. On a tool-selected shot this is not an undo — it steps back to
                    the record the old merge would have used, which is the escape hatch when
                    the tool's choice looks wrong. */}
                {data && data.IsEdited && (
                    <Button color="warning" outline disabled={saving} onClick={() => apply({ reset: true })}>
                        {resetLabel}
                    </Button>
                )}
                {recommendable && recommended !== selectedIndex && (
                    <Button color="success" outline disabled={saving}
                        onClick={() => setSelectedIndex(recommended)}>
                        Select recommended{wakes ? '' : ` (#${recommended})`}
                    </Button>
                )}
                <Button color="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button
                    color="primary"
                    disabled={!canApply || saving}
                    onClick={() => apply({ gyroIndex: selectedIndex })}>
                    {applyLabel}
                </Button>
            </ModalFooter>
        </Modal>
    );
};
